import { useId, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getSession, logout } from "../lib/api";
import { useAuthDialog } from "./AuthDialog";
import "./header-badges.css";

const navigation = [
  ["/", "Home"],
  ["/blog", "Blog"],
  ["/courses", "Courses"],
  ["/projects", "Projects"],
  ["/resume", "Resume"],
  ["/contact", "Contact"]
] as const;

export function Header({ beforeLogout }: { beforeLogout?: () => boolean | Promise<boolean> } = {}) {
  const { openLogin } = useAuthDialog();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      await queryClient.cancelQueries();
      queryClient.clear();
      queryClient.setQueryData(["session"], null);
      setMenuOpen(false);
      await navigate({ to: "/" });
    }
  });
  const { data: session, isPending } = useQuery({ queryKey: ["session"], queryFn: getSession, staleTime: 30_000, refetchOnWindowFocus: true, refetchInterval: (query) => query.state.data ? 240_000 : false });
  const accountLabel = session?.isAdmin ? "Backoffice" : "My profile";
  const accountIcon = <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="8" r="3.3" stroke="currentColor" strokeWidth="1.5" /><path d="M5 20v-2a7 7 0 0 1 14 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const menuButton = useRef<HTMLButtonElement>(null);

  return (
    <header
      className="site-header premium-header nm-site-header"
      onKeyDown={(event) => {
        if (event.key === "Escape" && menuOpen) {
          setMenuOpen(false);
          menuButton.current?.focus();
        }
      }}
    >
      <Link className="nm-brand" to="/" onClick={() => setMenuOpen(false)} aria-label="Nuno Miranda — home">
        <svg className="nm-brand-mark" viewBox="0 0 48 40" fill="none" aria-hidden="true">
          <path d="m13 10-10 10 10 10M29 5 19 35" stroke="#35d5f4" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          <path d="m35 10 10 10-10 10" stroke="#b576ff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="nm-brand-copy"><b>Nuno Miranda</b><small>Fullstack Developer</small></span>
      </Link>

      <button
        className="nm-menu-toggle"
        type="button"
        aria-expanded={menuOpen}
        aria-controls={menuId}
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        onClick={() => setMenuOpen((open) => !open)}
        ref={menuButton}
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d={menuOpen ? "m6 6 12 12M6 18 18 6" : "M4 6h16M4 12h16M4 18h16"} stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      </button>

      <nav id={menuId} className="nm-navigation" data-open={menuOpen} aria-label="Main navigation">
        {navigation.map(([to, label]) => (
          <Link
            key={to}
            to={to}
            activeOptions={{ exact: to === "/" }}
            activeProps={{ "aria-current": "page", className: "nm-nav-active" }}
            onClick={() => setMenuOpen(false)}
          >
            {label}
          </Link>
        ))}
      </nav>

      <div className="nm-header-actions">
        {session ? (
          <Link className="nm-login-link" to={session.isAdmin ? "/admin" : "/profile"} aria-label={accountLabel} title={accountLabel} onClick={() => setMenuOpen(false)}>
            {accountIcon}<span>{accountLabel}</span>
          </Link>
        ) : (
          <button className="nm-login-link" type="button" aria-label="Sign in to my account" aria-haspopup="dialog" disabled={isPending} onClick={() => { setMenuOpen(false); openLogin(); }}>
            {accountIcon}<span>Sign in</span>
          </button>
        )}
        {session ? <button type="button" className="nm-logout" aria-label="Sign out" title="Sign out" disabled={logoutMutation.isPending} onClick={async () => { if (!beforeLogout || await beforeLogout()) logoutMutation.mutate(); }}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M10 5H5v14h5m4-11 4 4-4 4m-5-4h12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          <span>{logoutMutation.isPending ? "Signing out…" : "Sign out"}</span>
        </button> : null}
        {logoutMutation.isError ? <p className="nm-logout-error" role="alert">Unable to sign out. Try again.</p> : null}
        <Link className="nm-header-cta" to="/contact">
          <span aria-hidden="true">&gt;_</span> Get in touch
        </Link>
      </div>
    </header>
  );
}
